import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { directorySearchRootQueryKey, type DirectoryEntity } from "./query-keys";

/**
 * Write an authoritative mutation result into an already-loaded full
 * directory. If the directory has not loaded yet, leave it empty so its first
 * observer still performs a complete fetch rather than treating one inserted
 * row as the whole directory.
 */
export function upsertDirectoryRecord<T extends { id: string }>(
    queryClient: QueryClient,
    entity: DirectoryEntity,
    queryKey: QueryKey,
    record: T,
): void {
    const current = queryClient.getQueryData<T[]>(queryKey);
    if (current) {
        const found = current.some(row => row.id === record.id);
        queryClient.setQueryData<T[]>(queryKey, found
            ? current.map(row => row.id === record.id ? record : row)
            : [record, ...current]);
    } else {
        void queryClient.invalidateQueries({ queryKey, exact: true });
    }

    // Active search surfaces refetch immediately; inactive ones are marked
    // stale so reopening Companies or Command-K cannot reuse old results.
    void queryClient.invalidateQueries({ queryKey: directorySearchRootQueryKey(entity) });
}

export function removeDirectoryRecord<T extends { id: string }>(
    queryClient: QueryClient,
    entity: DirectoryEntity,
    queryKey: QueryKey,
    id: string,
): void {
    const current = queryClient.getQueryData<T[]>(queryKey);
    if (current) {
        queryClient.setQueryData<T[]>(queryKey, current.filter(row => row.id !== id));
    } else {
        void queryClient.invalidateQueries({ queryKey, exact: true });
    }
    void queryClient.invalidateQueries({ queryKey: directorySearchRootQueryKey(entity) });
}
